import { useCallback } from 'react'

import { NavigateOptions } from 'react-router-dom'

import logger from '@cash-web/shared/util-logger'
import { useJourneyContext } from '@cash-web/shared-journey-data-access-context'
import { JourneyScope } from '@cash-web/shared-journey-util-main'

import { isClientRoute } from './isClientRoute'
import { transformReceiptUrl } from './transformReceiptUrl'

export type LoadUrlOptions = NavigateOptions & { scope?: Partial<JourneyScope> }
export type LoadUrlProps = { trackingName?: string; url?: string; options?: LoadUrlOptions; target?: string }

export type LoadUrlLocationState = Partial<{
  clientRouteReferrerPath: string
}>

export const useSafeHook =
  <T extends (...args: unknown[]) => unknown>(hook: T) =>
  (...args: Parameters<T>) => {
    try {
      return hook(...args) as ReturnType<T>
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (error) {
      return undefined
    }
  }

export const useLoadUrl = () => {
  const journey = useJourneyContext()

  const loadUrl = useCallback(
    async ({ url, options, target = '_blank' }: LoadUrlProps) => {
      try {
        if (!url) {
          return
        }

        const parsedUrl = new URL(url)
        const decodedPathname = decodeURIComponent(parsedUrl.pathname)
        const pathnameWithSearch = decodedPathname + parsedUrl.search

        if (isClientRoute(url) && decodedPathname) {
          await journey.navigate(pathnameWithSearch, options)
        } else {
          window.open(transformReceiptUrl(url), target)
        }
      } catch (error) {
        logger.error(new Error(`Error in loadUrl: ${error instanceof Error ? error.message : error}`))
      }
    },
    [journey]
  )
  return loadUrl
}
